// The one cached order in localStorage (`hw_pending_orderv2`), and which venue
// it belongs to.
//
// The cache holds a single order and carries no venue scoping of its own, so an
// order placed at restaurant A was restored on restaurant B's menu: A's
// confirmation popped over B's checkout and the page navigated back to the
// menu, which stopped the guest ordering at B at all.
//
// That was a narrow window while an order resolved in minutes. A theater
// PRE-ORDER stays pending until curtain — days, sometimes — so at a theater it
// is the normal state rather than an edge case.
//
// Lives here rather than inline for a second reason: RestaurantMenu is ~4,000
// lines and eslint-plugin-react-hooks 4.3.0 gives up analysing it once the
// component body carries a few more branches, then reports every hook below
// ~line 2500 as conditional and fails the build. See the note in
// utils/stampcard.js.

/**
 * Should a cached pending order be restored on this venue's page?
 *
 * Unknown ids fail OPEN — an order shape without `restaurantId` (or a page
 * whose restaurant has not loaded yet) keeps the behaviour it had before this
 * check existed, rather than silently hiding a real pending order.
 */
export const isPendingOrderForVenue = (order, restaurantId) => {
  if (!order || !order.restaurantId || !restaurantId) return true;
  return String(order.restaurantId) === String(restaurantId);
};

/**
 * The cached order, parsed, but only if it belongs to this venue — otherwise
 * null, exactly as if nothing were cached.
 *
 * Returning null rather than making the caller test twice is deliberate: it
 * folds the venue check into the `if (cachedOrder)` the caller already had,
 * which is what keeps RestaurantMenu inside the hook linter's budget.
 * Malformed JSON is treated as no cache at all.
 */
export const readPendingOrderForVenue = (raw, restaurantId) => {
  try {
    const order = raw ? JSON.parse(raw) : null;
    return isPendingOrderForVenue(order, restaurantId) ? order : null;
  } catch (err) {
    return null;
  }
};

/**
 * Which performance the order-confirmation modal should name.
 *
 * The modal is a receipt for an order already placed, so it must read that
 * order's own performance — reading the page's live selection made it name
 * whichever show the guest happened to be looking at, which at a theater is
 * routinely a different one at a different venue.
 *
 * Falls back to the current selection for an order placed before the payload
 * carried a performance, and for the moment between paying and the order
 * details arriving.
 */
export const receiptPerformance = (placedOrder, selectedPerformance) =>
  (placedOrder && placedOrder.theaterPerformance) || selectedPerformance || null;
